import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Constants from "../../constants";
import { ShareContext } from "../../context/ShareContext";
import BackLoader from "../../components/backloader";
import JisButton from "../../components/controls/jisButton";
import JisDialogForm from "../../components/formdialog";
import JisTabButton from "../../components/controls/jisTabButton";
import JisEmptyList from "../../components/controls/emptylist";
import PdfViewer from "../../components/controls/pdfviewer";
import { api, docLink, fileToBase64, fmtDate, fmtDateTime, tone } from "./hctc_common";
import "./hctc.css";

// One historical case: overview, parties/claims, digitized documents, links, applications and the audit trail.
// Buttons appear only for the function screens the role holds (hist_register, hist_verify, hist_digitize, hist_link, hist_migrate).
export default function HistoricalCaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, setIsExpired } = useContext(ShareContext);
  const [d, setD] = useState(null);
  const [lk, setLk] = useState(null);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [dlg, setDlg] = useState(null); // {kind, ...}
  const [preview, setPreview] = useState("");

  const load = async () => {
    setLoading(true);
    const [x, l] = await Promise.all([api(token, setIsExpired, `historical/${id}`), lk ? Promise.resolve(null) : api(token, setIsExpired, "historical/lookups")]);
    setLoading(false);
    if (l?.code === 0) setLk(l);
    if (x.code === 0) setD(x);
    else if (x.code !== -10) setErr(x.status || "Unable to load the record");
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const has = (c) => d?.codes?.includes(c);
  const it = d?.item;
  const vs = it?.verificationStatus;

  const run = async (path, body, okMsg) => {
    setErr("");
    setMsg("");
    setLoading(true);
    const r = await api(token, setIsExpired, path, { method: "POST", body });
    setLoading(false);
    if (r.code === 0) {
      setDlg(null);
      setMsg(okMsg || "Saved");
      load();
      return r;
    }
    if (r.code !== -10) setErr(r.status || "The action failed");
    return null;
  };

  const doAction = (action, extra = {}) => run(`historical/action/${id}`, { action, ...extra }, "Updated");

  const onMigrate = async () => {
    const r = await run(`historical/migrate/${id}`, {}, "Migrated to JIS");
    if (r) setMsg(`Migrated to JIS. Reference number ${r.referenceNumber}.`);
  };

  if (!d) {
    return (
      <div className="hctc-page">
        {err && <div className="hctc-err">{err}</div>}
        <BackLoader open={loading} />
      </div>
    );
  }

  const kv = (k, v) => (
    <>
      <div className="k">{k}</div>
      <div className="v">{v || <span className="hctc-muted">-</span>}</div>
    </>
  );

  const editable = has("hist_register") && ["Draft", "Rework Required"].includes(vs) || has("hist_verify") && !["Rejected", "Archived"].includes(vs);

  return (
    <div className="hctc-page">
      <div className="hctc-toolbar">
        <div className="left">
          <JisButton variant="plain" onClick={() => navigate("/historical-cases")}>
            Back
          </JisButton>
          <div>
            <h2>
              {it.originalCaseNumber || it.legacySuitNumber || it.legacyCaseNumber} <span className="hctc-muted">({it.historicalRef})</span>
            </h2>
            <div>
              <span className="hctc-pill info">Historical</span>
              <span className={`hctc-pill ${tone(vs)}`}>{vs}</span>
              <span className={`hctc-pill ${tone(it.digitizationStatus)}`}>{it.digitizationStatus}</span>
              {it.availabilityStatus && <span className={`hctc-pill ${tone(it.availabilityStatus)}`}>{it.availabilityStatus}</span>}
              {it.recordStatus !== "Active" && <span className={`hctc-pill ${tone(it.recordStatus)}`}>{it.recordStatus}</span>}
              {it.isMigrated && <span className="hctc-pill good">Migrated: {it.jisCaseNumber}</span>}
            </div>
          </div>
        </div>
        <div className="right hctc-actions">
          {editable && !it.isMigrated && (
            <JisButton variant="outline" onClick={() => navigate(`/historical-cases/edit/${id}`)}>
              Edit
            </JisButton>
          )}
          {has("hist_register") && ["Draft", "Rework Required"].includes(vs) && <JisButton onClick={() => doAction("submit")}>Submit for verification</JisButton>}
          {has("hist_verify") && !["Rejected", "Archived"].includes(vs) && (
            <>
              <JisButton onClick={() => setDlg({ kind: "action", action: "verify", title: "Mark verified" })}>Verify</JisButton>
              <JisButton variant="outline" onClick={() => setDlg({ kind: "action", action: "partial", title: "Mark partially verified" })}>
                Partially verify
              </JisButton>
              <JisButton variant="outline" onClick={() => setDlg({ kind: "action", action: "rework", title: "Return for correction", required: true })}>
                Return
              </JisButton>
              <JisButton variant="outline" onClick={() => setDlg({ kind: "action", action: "reject", title: "Reject unsupported record", required: true })}>
                Reject
              </JisButton>
              <JisButton variant="plain" onClick={() => setDlg({ kind: "action", action: "archive", title: "Archive record" })}>
                Archive
              </JisButton>
            </>
          )}
          {has("hist_verify") && ["Rejected", "Archived"].includes(vs) && (
            <JisButton variant="outline" onClick={() => setDlg({ kind: "action", action: "reopen", title: "Reopen record" })}>
              Reopen
            </JisButton>
          )}
          {(has("hist_verify") || has("hist_digitize")) && (
            <JisButton variant="outline" onClick={() => setDlg({ kind: "availability", value: it.availabilityStatus || "" })}>
              Availability
            </JisButton>
          )}
          {has("hist_migrate") && vs === "Verified" && !it.isMigrated && !it.linkedCaseId && (
            <JisButton onClick={() => setDlg({ kind: "migrate" })}>Migrate to JIS</JisButton>
          )}
        </div>
      </div>
      {msg && <div className="hctc-ok">{msg}</div>}
      {err && <div className="hctc-err">{err}</div>}

      <div className="tab_body">
        <div className="tab_section" style={{ display: "flex", flexWrap: "wrap", marginBottom: 16 }}>
          {[
            ["overview", "Overview"],
            ["parties", `Parties & Claims (${d.parties.length})`],
            ["documents", `Documents (${d.documents.filter((x) => x.isCurrent).length})`],
            ["links", `Links (${d.links.length})`],
            ["applications", `Applications (${d.applications.length})`],
            ["audit", "Audit Trail"],
          ].map(([k, t]) => (
            <JisTabButton key={k} isRoleBased={false} title={t} isActive={tab === k} onClick={() => setTab(k)} />
          ))}
        </div>

        {tab === "overview" && (
          <div className="hctc-grid wide">
            <div className="hctc-card">
              <div className="head">
                <h3>Identifiers</h3>
              </div>
              <div className="hctc-kv">
                {kv("Original case number", it.originalCaseNumber)}
                {kv("Legacy suit number", it.legacySuitNumber)}
                {kv("Legacy case number(s)", it.legacyCaseNumber)}
                {kv("JIS historical reference", it.historicalRef)}
                {kv("Linked JIS case", d.jisCase ? `${d.jisCase.caseNumber || ""} (${d.jisCase.referenceNumber})` : null)}
                {kv("Case title", it.caseTitle)}
              </div>
            </div>
            <div className="hctc-card">
              <div className="head">
                <h3>Filing</h3>
              </div>
              <div className="hctc-kv">
                {kv("Original filing date", it.originalFilingDate ? fmtDate(it.originalFilingDate) : it.filingDateStatus)}
                {kv("Date recorded on JIS", fmtDateTime(it.createdOn))}
                {kv("Court", d.names.court)}
                {kv("Judicial division", d.names.division)}
                {kv("Judge", d.judge ? Constants.judgeName(d.judge) : it.judgeName)}
                {kv("Registrar", it.registrarName)}
                {kv("Original filing registry", it.originalFilingRegistry)}
                {kv("Case type", d.names.caseType)}
                {kv("Category", [d.names.caseCategory, d.names.caseSubCategory].filter(Boolean).join(" / "))}
                {kv("Case origin", d.names.caseOrigin)}
              </div>
            </div>
            <div className="hctc-card">
              <div className="head">
                <h3>Record</h3>
              </div>
              <div className="hctc-kv">
                {kv("Record source", it.recordSource)}
                {kv("Intake", it.intakeChannel)}
                {kv("Physical file number", it.physicalFileNumber)}
                {kv("Archive reference", it.archiveReferenceNumber)}
                {kv("Archive location", it.archiveLocation)}
                {kv("Registered by", `${d.names.createdBy || ""} ${fmtDate(it.createdOn)}`)}
                {kv("Verified by", it.verifiedOn ? `${d.names.verifiedBy || ""} ${fmtDate(it.verifiedOn)}` : null)}
                {kv("Last amended by", it.updatedOn ? `${d.names.updatedBy || ""} ${fmtDate(it.updatedOn)}` : null)}
                {kv("Notes", it.notes)}
              </div>
            </div>
          </div>
        )}

        {tab === "parties" && (
          <>
            <div className="hctc-card">
              <div className="head">
                <h3>Parties</h3>
              </div>
              <table className="case-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Name</th>
                    <th>Alternative spellings</th>
                    <th>Contact</th>
                  </tr>
                </thead>
                <tbody>
                  {d.parties.map((p) => (
                    <tr key={p.id}>
                      <td>{p.partyTypeTitle || "-"}</td>
                      <td>
                        {p.name} {p.isOrg && <span className="hctc-pill plain">Org</span>} {p.isLead && <span className="hctc-pill info">Lead</span>}
                      </td>
                      <td>{p.alternativeNames || "-"}</td>
                      <td>{[p.email, p.phone, p.address].filter(Boolean).join(" / ") || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {d.parties.length === 0 && <JisEmptyList title="No parties recorded" />}
            </div>
            <div className="hctc-card">
              <div className="head">
                <h3>Claims / reliefs</h3>
              </div>
              {d.claims.length === 0 ? (
                <JisEmptyList title="No claims recorded" />
              ) : (
                <ol>
                  {d.claims.map((c) => (
                    <li key={c.id}>
                      {c.claim} {c.claimValue && <span className="hctc-muted">({c.claimValue})</span>}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </>
        )}

        {tab === "documents" && (
          <div className="hctc-card">
            <div className="head">
              <h3>Digitized documents</h3>
              {has("hist_digitize") && (
                <div className="hctc-actions">
                  <select
                    value={it.digitizationStatus}
                    onChange={(e) => doAction("digitization", { digitizationStatus: e.target.value })}
                    style={{ padding: 6, borderRadius: 8 }}
                    title="Digitization status"
                  >
                    {(lk?.digitizationStatuses || []).map((x) => (
                      <option key={x}>{x}</option>
                    ))}
                  </select>
                  <JisButton onClick={() => setDlg({ kind: "upload" })}>Upload / scan document</JisButton>
                </div>
              )}
            </div>
            <div className="table-wrapper" style={{ overflowX: "auto" }}>
              <table className="case-table hctc-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Title</th>
                    <th>Original date</th>
                    <th>Digitized</th>
                    <th>Pages</th>
                    <th>Source / location</th>
                    <th>Version</th>
                    <th>Verification</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {d.documents.map((x) => (
                    <tr key={x.id} style={{ opacity: x.isCurrent ? 1 : 0.55 }}>
                      <td>{x.documentType}</td>
                      <td className="wrap">{x.documentTitle}</td>
                      <td>{fmtDate(x.originalDocumentDate) || "-"}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {fmtDate(x.dateDigitized)}
                        <div className="hctc-muted">{x.uploadedBy}</div>
                      </td>
                      <td>{x.pageCount}</td>
                      <td>
                        {x.sourceType || "-"}
                        <div className="hctc-muted">{[x.physicalFileReference, x.archiveLocation].filter(Boolean).join(" / ")}</div>
                      </td>
                      <td>
                        v{x.version} {!x.isCurrent && <span className="hctc-pill plain">superseded</span>}
                      </td>
                      <td>
                        <span className={`hctc-pill ${tone(x.verificationStatus)}`}>{x.verificationStatus}</span>
                        <div className="hctc-muted" title={x.fileHash}>
                          OCR: {x.ocrStatus}
                        </div>
                      </td>
                      <td>
                        <div className="hctc-row-actions">
                          <JisButton variant="plain" onClick={() => setPreview(docLink(x.url))}>
                            View
                          </JisButton>
                          {has("hist_digitize") && x.isCurrent && (
                            <JisButton variant="plain" onClick={() => setDlg({ kind: "upload", replace: x })}>
                              New version
                            </JisButton>
                          )}
                          {has("hist_verify") && x.isCurrent && x.verificationStatus !== "Verified" && (
                            <JisButton variant="plain" onClick={() => run(`historical/document-verify/${x.id}`, { action: "verify" }, "Document verified")}>
                              Verify
                            </JisButton>
                          )}
                          {has("hist_verify") && x.isCurrent && x.verificationStatus !== "Rejected" && (
                            <JisButton variant="plain" onClick={() => setDlg({ kind: "docreject", doc: x })}>
                              Reject
                            </JisButton>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {d.documents.length === 0 && <JisEmptyList title="No documents digitized yet" />}
            </div>
          </div>
        )}

        {tab === "links" && (
          <div className="hctc-card">
            <div className="head">
              <h3>Linked records</h3>
              <div className="hctc-actions">
                {has("hist_verify") && (
                  <JisButton variant="outline" onClick={() => setDlg({ kind: "recordstatus", value: it.recordStatus })}>
                    Record status
                  </JisButton>
                )}
                {has("hist_link") && <JisButton onClick={() => setDlg({ kind: "link" })}>Link record</JisButton>}
              </div>
            </div>
            <div className="hctc-info">Duplicate and merged records are never deleted. They are marked and linked, and every link is kept in the audit trail.</div>
            <table className="case-table">
              <thead>
                <tr>
                  <th>Link type</th>
                  <th>Linked record</th>
                  <th>Reason</th>
                  <th>Requested</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {d.links.map((l) => (
                  <tr key={l.id}>
                    <td>{l.linkType}</td>
                    <td>
                      {l.jisCase && (
                        <span>
                          JIS: {l.jisCase.caseNumber || l.jisCase.referenceNumber} - {l.jisCase.caseTitle}
                        </span>
                      )}
                      {l.historical && (
                        <a href={`/historical-cases/${l.historical.id}`}>
                          {l.historical.historicalRef} {l.historical.originalCaseNumber} - {l.historical.caseTitle}
                        </a>
                      )}
                      {l.historicalCaseId !== Number(id) && <div className="hctc-muted">(link made from the other record)</div>}
                    </td>
                    <td className="wrap">{l.reason}</td>
                    <td>
                      {fmtDate(l.createdOn)}
                      <div className="hctc-muted">{l.createdBy}</div>
                    </td>
                    <td>
                      <span className={`hctc-pill ${tone(l.status === "Approved" ? "Approved" : l.status)}`}>{l.status}</span>
                    </td>
                    <td>
                      {has("hist_verify") && l.status === "Pending" && (
                        <div className="hctc-row-actions">
                          <JisButton variant="plain" onClick={() => run(`historical/link-decision/${l.id}`, { approve: true }, "Link approved")}>
                            Approve
                          </JisButton>
                          <JisButton variant="plain" onClick={() => run(`historical/link-decision/${l.id}`, { approve: false }, "Link rejected")}>
                            Reject
                          </JisButton>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {d.links.length === 0 && <JisEmptyList title="No linked records" />}
          </div>
        )}

        {tab === "applications" && (
          <div className="hctc-card">
            <table className="case-table">
              <thead>
                <tr>
                  <th>Application</th>
                  <th>Service</th>
                  <th>Status</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {d.applications.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <a href={`/service-desk/app/${a.id}`}>{a.applicationNumber}</a>
                    </td>
                    <td>{a.requestType}</td>
                    <td>
                      <span className={`hctc-pill ${tone(a.status)}`}>{a.status}</span>
                    </td>
                    <td>{fmtDate(a.submittedOn)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {d.applications.length === 0 && <JisEmptyList title="No CTC, decree or search applications on this record" />}
          </div>
        )}

        {tab === "audit" && (
          <div className="hctc-card">
            <ul className="hctc-timeline">
              {d.audit.map((a) => (
                <li key={a.id}>
                  <div>
                    <b>{a.action}</b>
                    {a.previousStatus !== a.newStatus && a.newStatus && (
                      <span className="hctc-muted">
                        {" "}
                        {a.previousStatus || "-"} → {a.newStatus}
                      </span>
                    )}
                  </div>
                  {a.comments && <div>{a.comments}</div>}
                  <div className="when">
                    {fmtDateTime(a.createdOn)} · {a.userName} {a.userRole && `(${a.userRole})`} {a.ipAddress && `· ${a.ipAddress}`}
                    {a.uploadedFiles && ` · ${a.uploadedFiles}`}
                  </div>
                </li>
              ))}
            </ul>
            {d.audit.length === 0 && <JisEmptyList title="No activity" />}
          </div>
        )}
      </div>

      {dlg?.kind === "action" && <ActionDialog dlg={dlg} onClose={() => setDlg(null)} onRun={(comment) => doAction(dlg.action, { comment })} />}
      {dlg?.kind === "availability" && (
        <SelectDialog
          title="Record availability"
          options={lk?.availabilityStatuses || []}
          value={dlg.value}
          onClose={() => setDlg(null)}
          onRun={(value, comment) => doAction("availability", { availabilityStatus: value, comment })}
        />
      )}
      {dlg?.kind === "recordstatus" && (
        <SelectDialog
          title="Record status"
          options={lk?.recordStatuses || []}
          value={dlg.value}
          required
          onClose={() => setDlg(null)}
          onRun={(value, comment) => run(`historical/record-status/${id}`, { action: value, comment }, "Record status changed")}
        />
      )}
      {dlg?.kind === "docreject" && (
        <ActionDialog
          dlg={{ title: `Reject "${dlg.doc.documentTitle}"`, required: true }}
          onClose={() => setDlg(null)}
          onRun={(comment) => run(`historical/document-verify/${dlg.doc.id}`, { action: "reject", comment }, "Document rejected")}
        />
      )}
      {dlg?.kind === "upload" && (
        <UploadDialog lk={lk} item={it} replace={dlg.replace} onClose={() => setDlg(null)} onRun={(body) => run(`historical/document/${id}`, body, "Document uploaded")} />
      )}
      {dlg?.kind === "link" && (
        <LinkDialog lk={lk} token={token} setIsExpired={setIsExpired} selfId={Number(id)} onClose={() => setDlg(null)} onRun={(body) => run(`historical/link/${id}`, body, "Link saved")} />
      )}
      {dlg?.kind === "migrate" && (
        <JisDialogForm title="Migrate to JIS" onClose={() => setDlg(null)} minWidth="35%">
          <div className="hctc-dialog-body">
            <p>
              This creates a JIS case for <b>{it.caseTitle}</b> with a JIS reference number, carries over the parties, claims and current documents, and keeps it marked
              as historical. The original case number stays the primary identifier.
            </p>
            {!it.caseTypeId && <div className="hctc-warn">Set the case type (Edit) before migrating.</div>}
            <div className="hctc-actions">
              <JisButton onClick={onMigrate} disabled={!it.caseTypeId}>
                Migrate
              </JisButton>
              <JisButton variant="outline" onClick={() => setDlg(null)}>
                Cancel
              </JisButton>
            </div>
          </div>
        </JisDialogForm>
      )}
      {preview && (
        <JisDialogForm title="Document" onClose={() => setPreview("")}>
          <div style={{ height: "80vh", minWidth: "min(900px, 85vw)" }}>
            <PdfViewer pdfUrl={preview} />
          </div>
        </JisDialogForm>
      )}
      <BackLoader open={loading} />
    </div>
  );
}

function ActionDialog({ dlg, onClose, onRun }) {
  const [comment, setComment] = useState("");
  return (
    <JisDialogForm title={dlg.title} onClose={onClose} minWidth="35%">
      <div className="hctc-dialog-body">
        <div className="hctc-field">
          <label>
            Comment{dlg.required && <span className="req">*</span>}
          </label>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} />
        </div>
        <div className="hctc-actions" style={{ marginTop: 12 }}>
          <JisButton onClick={() => onRun(comment)} disabled={dlg.required && !comment.trim()}>
            Confirm
          </JisButton>
          <JisButton variant="outline" onClick={onClose}>
            Cancel
          </JisButton>
        </div>
      </div>
    </JisDialogForm>
  );
}

function SelectDialog({ title, options, value, required, onClose, onRun }) {
  const [v, setV] = useState(value || "");
  const [comment, setComment] = useState("");
  return (
    <JisDialogForm title={title} onClose={onClose} minWidth="35%">
      <div className="hctc-dialog-body">
        <div className="hctc-field">
          <label>Status</label>
          <select value={v} onChange={(e) => setV(e.target.value)}>
            <option value="">Select</option>
            {options.map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </div>
        <div className="hctc-field" style={{ marginTop: 10 }}>
          <label>
            Comment{required && <span className="req">*</span>}
          </label>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} />
        </div>
        <div className="hctc-actions" style={{ marginTop: 12 }}>
          <JisButton onClick={() => onRun(v, comment)} disabled={!v || (required && !comment.trim())}>
            Save
          </JisButton>
          <JisButton variant="outline" onClick={onClose}>
            Cancel
          </JisButton>
        </div>
      </div>
    </JisDialogForm>
  );
}

function UploadDialog({ lk, item, replace, onClose, onRun }) {
  const [f, setF] = useState({
    documentType: replace?.documentType || "",
    documentTitle: replace?.documentTitle || "",
    originalDocumentDate: "",
    sourceType: replace?.sourceType || item.recordSource || "",
    physicalFileReference: item.physicalFileNumber || "",
    archiveLocation: item.archiveLocation || "",
    ocrStatus: "Not Applied",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const on = (e) => setF({ ...f, [e.target.name]: e.target.value });
  const submit = async () => {
    if (!file) return;
    setBusy(true);
    const base64 = await fileToBase64(file);
    await onRun({
      ...f,
      originalDocumentDate: f.originalDocumentDate || null,
      base64,
      fileName: file.name,
      replaceDocumentId: replace?.id || null,
    });
    setBusy(false);
  };
  return (
    <JisDialogForm title={replace ? `New version of "${replace.documentTitle}"` : "Upload / scan document"} onClose={onClose} minWidth="45%">
      <div className="hctc-dialog-body">
        <div className="hctc-grid">
          <div className="hctc-field">
            <label>
              Document category<span className="req">*</span>
            </label>
            <select name="documentType" value={f.documentType} onChange={on}>
              <option value="">Select</option>
              {(lk?.documentCategories || []).map((x) => (
                <option key={x}>{x}</option>
              ))}
            </select>
          </div>
          <div className="hctc-field span2">
            <label>Title</label>
            <input name="documentTitle" value={f.documentTitle} onChange={on} />
          </div>
          <div className="hctc-field">
            <label>Original document date</label>
            <input type="date" name="originalDocumentDate" value={f.originalDocumentDate} onChange={on} />
            <div className="hint">Kept apart from the digitization date</div>
          </div>
          <div className="hctc-field">
            <label>Source</label>
            <select name="sourceType" value={f.sourceType} onChange={on}>
              <option value="">Select</option>
              {(lk?.recordSources || []).map((x) => (
                <option key={x}>{x}</option>
              ))}
            </select>
          </div>
          <div className="hctc-field">
            <label>OCR</label>
            <select name="ocrStatus" value={f.ocrStatus} onChange={on}>
              {["Not Applied", "Completed", "Failed", "Not Available"].map((x) => (
                <option key={x}>{x}</option>
              ))}
            </select>
          </div>
          <div className="hctc-field">
            <label>Physical file reference</label>
            <input name="physicalFileReference" value={f.physicalFileReference} onChange={on} />
          </div>
          <div className="hctc-field">
            <label>Archive location</label>
            <input name="archiveLocation" value={f.archiveLocation} onChange={on} />
          </div>
          <div className="hctc-field full">
            <label>
              File (PDF, PNG or JPG)<span className="req">*</span>
            </label>
            <input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <div className="hint">Pages are counted and a file hash is recorded. Large scans are compressed automatically.</div>
          </div>
        </div>
        <div className="hctc-actions" style={{ marginTop: 12 }}>
          <JisButton onClick={submit} disabled={!file || !f.documentType} loading={busy}>
            Upload
          </JisButton>
          <JisButton variant="outline" onClick={onClose}>
            Cancel
          </JisButton>
        </div>
      </div>
    </JisDialogForm>
  );
}

function LinkDialog({ lk, token, setIsExpired, selfId, onClose, onRun }) {
  const [linkType, setLinkType] = useState("");
  const [reason, setReason] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const search = async () => {
    setBusy(true);
    setMsg("");
    const r = await api(token, setIsExpired, "historical/unified-search", { method: "POST", body: { text: q } });
    setBusy(false);
    if (r.code === 0) setResults((r.results || []).filter((x) => !(x.source === "historical" && x.historicalCaseId === selfId)));
    else setMsg(r.status || "Search failed");
  };
  const submit = () =>
    onRun({
      linkType,
      reason,
      linkedCaseId: pick?.source === "jis" ? pick.caseId : null,
      linkedHistoricalCaseId: pick?.source === "historical" ? pick.historicalCaseId : null,
    });
  return (
    <JisDialogForm title="Link record" onClose={onClose} minWidth="55%">
      <div className="hctc-dialog-body">
        <div className="hctc-grid">
          <div className="hctc-field">
            <label>
              Link type<span className="req">*</span>
            </label>
            <select value={linkType} onChange={(e) => setLinkType(e.target.value)}>
              <option value="">Select</option>
              {(lk?.linkTypes || []).map((x) => (
                <option key={x}>{x}</option>
              ))}
            </select>
          </div>
          <div className="hctc-field span2">
            <label>Find the JIS case or historical record</label>
            <div style={{ display: "flex", gap: 6 }}>
              <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="case number, title or file number" />
              <JisButton onClick={search} loading={busy}>
                Search
              </JisButton>
            </div>
          </div>
        </div>
        {msg && <div className="hctc-err">{msg}</div>}
        {results.length > 0 && (
          <div style={{ maxHeight: 260, overflowY: "auto", margin: "10px 0" }}>
            <table className="case-table">
              <tbody>
                {results.map((r) => (
                  <tr key={r.key} onClick={() => setPick(r)} style={{ cursor: "pointer", outline: pick?.key === r.key ? "2px solid var(--primary)" : "none" }}>
                    <td>
                      <input type="radio" readOnly checked={pick?.key === r.key} />
                    </td>
                    <td>
                      {r.number}
                      <div className="hctc-muted">{r.reference}</div>
                    </td>
                    <td className="wrap">{r.title}</td>
                    <td>
                      {(r.labels || []).map((l) => (
                        <span key={l} className={`hctc-pill ${tone(l)}`}>
                          {l}
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="hctc-field">
          <label>
            Reason<span className="req">*</span>
          </label>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        <div className="hctc-actions" style={{ marginTop: 12 }}>
          <JisButton onClick={submit} disabled={!linkType || !pick || !reason.trim()}>
            Save link
          </JisButton>
          <JisButton variant="outline" onClick={onClose}>
            Cancel
          </JisButton>
        </div>
      </div>
    </JisDialogForm>
  );
}
